"use client";

import { useEffect, useState } from "react";
import { useAuth, useSignUp } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthCard, authLinkClassName, authPrimaryButtonClassName } from "@/components/auth/AuthCard";
import { PasswordField } from "@/components/auth/PasswordField";
import { VerificationCodeForm } from "@/components/auth/VerificationCodeForm";
import { useVerificationCode } from "@/hooks/useVerificationCode";
import { useNavigate } from "react-router";
import { Loader2, UserPlus } from "lucide-react";

export const SignUpPage: React.FC = () => {
  const OTP_LENGTH = 6;
  const { isSignedIn } = useAuth();
  const { signUp, fetchStatus } = useSignUp();

  const [error, setError] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isEmailVerificationStep, setIsEmailVerificationStep] = useState(false);

  const {
    verificationCode,
    verificationCodeDigits,
    verificationCodeInputRefs,
    resetVerificationCode,
    focusFirstVerificationCodeInput,
    handleVerificationCodeDigitChange,
    handleVerificationCodeKeyDown,
    handleVerificationCodePaste,
  } = useVerificationCode(OTP_LENGTH);

  const navigate = useNavigate();

  const handleCreateAccount = async () => {
    try {
      setError("");

      if (!signUp) return;

      const { error: signUpError } = await signUp.password({
        emailAddress: email,
        password,
        firstName,
        lastName,
      });

      if (signUpError) throw signUpError;

      const { error: emailCodeError } = await signUp.verifications.sendEmailCode();

      if (emailCodeError) throw emailCodeError;

      resetVerificationCode();
      setIsEmailVerificationStep(true);
      setError("A verification code has been sent to your email.");

      setTimeout(() => focusFirstVerificationCodeInput(), 100);
    } catch (err: any) {
      console.error("Sign up failed:", err);
      setError(err?.message || "Unable to create account. Please check your details.");
    }
  };

  const handleVerifyEmail = async () => {
    try {
      if (!signUp || verificationCode.length !== OTP_LENGTH) {
        setError("Please enter the complete 6-digit code.");
        return;
      }

      const { error: verificationError } = await signUp.verifications.verifyEmailCode({
        code: verificationCode,
      });

      if (verificationError) throw verificationError;

      if (signUp.status === "complete") {
        const { error: finalizeError } = await signUp.finalize();

        if (finalizeError) {
          throw finalizeError;
        }
        navigate("/", { replace: true });
        return;
      }

      setError(`Verification incomplete. Status: ${signUp.status}`);
    } catch (err: any) {
      console.error("Verification failed:", err);
      setError("Invalid code. Please try again.");
    }
  };

  useEffect(() => {
    if (isSignedIn) {
      navigate("/", { replace: true });
    }
  }, [isSignedIn, navigate]);

  return (
    <AuthCard error={error}>
            {!isEmailVerificationStep ? (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First name</Label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      autoComplete="given-name"
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last name</Label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      autoComplete="family-name"
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    className="h-12"
                  />
                </div>

                <PasswordField
                  id="password"
                  label="Password"
                  value={password}
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                  isVisible={showPassword}
                  onChange={setPassword}
                  onToggleVisibility={() => setShowPassword(!showPassword)}
                />

                <Button
                  onClick={handleCreateAccount}
                  disabled={
                    fetchStatus === "fetching" ||
                    !firstName.trim() ||
                    !lastName.trim() ||
                    !email.trim() ||
                    !password.trim()
                  }
                  className={authPrimaryButtonClassName}
                >
                  {fetchStatus === "fetching" ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      <UserPlus className="mr-2 h-5 w-5" />
                      Create Account
                    </>
                  )}
                </Button>

                <div className="text-center">
                  <a
                    href="/signin"
                    className={authLinkClassName}
                  >
                    Already have an account? <span className="font-medium">Sign in</span>
                  </a>
                </div>
              </div>
            ) : (
              /* Email Verification Step */
              <div className="space-y-6">
                <div className="flex flex-col items-center text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <UserPlus size={28} />
                  </div>
                  <h3 className="text-xl font-semibold">Verify your email</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    We sent a 6-digit code to <strong>{email}</strong>
                  </p>
                </div>

                <VerificationCodeForm
                  digits={verificationCodeDigits}
                  inputRefs={verificationCodeInputRefs}
                  onDigitChange={handleVerificationCodeDigitChange}
                  onKeyDown={handleVerificationCodeKeyDown}
                  onPaste={handleVerificationCodePaste}
                  onVerify={handleVerifyEmail}
                />

                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEmailVerificationStep(false);
                    setError("");
                  }}
                  className="w-full"
                >
                  Back to sign up
                </Button>
              </div>
            )}
    </AuthCard>
  );
};